import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { apiWordBloom } from '../utils/api';
import PageSEO from '../components/PageSEO';
import ShareModal from '../components/ShareModal';
import SeoContent from '../components/SeoContent';

const GUESSES = 12;        // words you get per game
const MIN_LEN = 4;

// Score a word: length tiers + pangram + bonus-petal reward (Blossom-inspired)
function scoreWord(word, letterSetSize, bonusLetter) {
  const len = word.length;
  let pts = len === 4 ? 2 : len === 5 ? 4 : len === 6 ? 6 : 12 + 3 * (len - 7);
  const pangram = new Set(word).size === 7 && letterSetSize === 7;
  if (pangram) pts += 7;
  const usedBonus = bonusLetter && word.includes(bonusLetter);
  if (usedBonus) pts += 5;
  return { pts, pangram, usedBonus };
}

// Petal positions: 6 petals on a ring, angle starts at top and goes clockwise
const RING = 96;
const petalTransform = (i) => {
  const angle = -90 + i * 60;
  return { angle, t: `translate(-50%, -50%) rotate(${angle}deg) translate(0, -${RING}px)` };
};

const PETAL_BG  = 'linear-gradient(165deg, #fb7faf 0%, #ec4899 100%)';
const BONUS_BG  = 'linear-gradient(165deg, #fde68a 0%, #f59e0b 100%)';
const CENTER_BG = 'radial-gradient(circle at 35% 30%, #fde047, #f59e0b)';

const RATINGS = [
  [0.90, '🌸 Full Bloom', '#db2777'],
  [0.70, '🌷 Blooming',   '#e11d48'],
  [0.50, '🌿 Budding',    '#16a34a'],
  [0.30, '🌱 Sprouting',  '#65a30d'],
  [0.00, '🌰 Seedling',   '#a16207'],
];

export default function WordBloom() {
  const [phase, setPhase]     = useState('idle');   // idle | loading | playing | done | error
  const [center, setCenter]   = useState('');
  const [petals, setPetals]   = useState([]);       // display order (shuffleable)
  const [bonus, setBonus]     = useState('');       // current bonus petal letter
  const [validSet, setValid]  = useState(new Set());
  const [par, setPar]         = useState(0);

  const [input, setInput]     = useState('');
  const [found, setFound]     = useState([]);       // {word, pts, pangram, usedBonus}
  const [score, setScore]     = useState(0);
  const [guessesLeft, setGuessesLeft] = useState(GUESSES);
  const [flash, setFlash]     = useState(null);     // {text, type:'good'|'bad'}
  const [popKey, setPopKey]   = useState('');       // letter currently animating
  const [showShare, setShowShare] = useState(false);

  const allLetters = useMemo(() => (center ? [center, ...petals] : []), [center, petals]);

  const flashMsg = useCallback((text, type) => {
    setFlash({ text, type });
    window.clearTimeout(flashMsg._t);
    flashMsg._t = window.setTimeout(() => setFlash(null), 1600);
  }, []);

  const startGame = useCallback(async () => {
    setPhase('loading');
    setFlash(null);
    try {
      const { data } = await apiWordBloom();
      const words = data.words || [];
      // par = best achievable from your 12 best words (base scoring, ignore rotating bonus)
      const scored = words
        .map(w => scoreWord(w, 7, null).pts)
        .sort((a, b) => b - a);
      const parScore = scored.slice(0, GUESSES).reduce((a, b) => a + b, 0);

      setCenter(data.center);
      setPetals(data.petals);
      setBonus(data.bonus);
      setValid(new Set(words));
      setPar(parScore);

      setInput('');
      setFound([]);
      setScore(0);
      setGuessesLeft(GUESSES);
      setShowShare(false);
      setPhase('playing');
    } catch (e) {
      setPhase('error');
    }
  }, []);

  const rotateBonus = useCallback((currentPetals, currentBonus) => {
    const others = currentPetals.filter(p => p !== currentBonus);
    return others[Math.floor(Math.random() * others.length)] || currentBonus;
  }, []);

  const addLetter = useCallback((ch) => {
    if (phase !== 'playing') return;
    setInput(prev => (prev.length >= 16 ? prev : prev + ch));
    setPopKey(ch + Math.random());
  }, [phase]);

  const submit = useCallback(() => {
    if (phase !== 'playing') return;
    const word = input.toLowerCase();
    if (!word) return;

    if (word.length < MIN_LEN)       return flashMsg(`Too short — ${MIN_LEN}+ letters`, 'bad');
    if (!word.includes(center))      return flashMsg(`Must use center "${center.toUpperCase()}"`, 'bad');
    if (found.some(f => f.word === word)) return flashMsg('Already found!', 'bad');
    if (!validSet.has(word))         return flashMsg('Not in word list', 'bad');

    const res = scoreWord(word, 7, bonus);
    setFound(prev => [{ word, ...res }, ...prev]);
    setScore(s => s + res.pts);
    setInput('');

    let note = `+${res.pts}`;
    if (res.pangram) note += ' · PANGRAM! 🌸';
    else if (res.usedBonus) note += ' · bonus +5 🌟';
    flashMsg(note, 'good');

    setBonus(b => rotateBonus(petals, b));
    setGuessesLeft(g => {
      const left = g - 1;
      if (left <= 0) setPhase('done');
      return left;
    });
  }, [phase, input, center, bonus, found, validSet, petals, flashMsg, rotateBonus]);

  const shuffle = useCallback(() => {
    setPetals(prev => {
      const a = [...prev];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    });
  }, []);

  // keyboard support
  useEffect(() => {
    if (phase !== 'playing') return;
    const onKey = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); submit(); }
      else if (e.key === 'Backspace') { e.preventDefault(); setInput(p => p.slice(0, -1)); }
      else if (/^[a-zA-Z]$/.test(e.key)) {
        const ch = e.key.toLowerCase();
        if (allLetters.includes(ch)) addLetter(ch);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, submit, addLetter, allLetters]);

  const ratio = par ? score / par : 0;
  const [, ratingLabel, ratingColor] = RATINGS.find(([t]) => ratio >= t) || RATINGS[RATINGS.length - 1];
  const pangramsFound = found.filter(f => f.pangram).length;

  // Rank ladder: convert the hidden % thresholds into real point targets (relative to par)
  const rankRows = useMemo(() => {
    if (!par) return [];
    return RATINGS.map(([t, label, color]) => ({
      label, color,
      min: Math.ceil(t * par),
      reached: score >= Math.ceil(t * par),
      isCurrent: label === ratingLabel,
    }));
  }, [par, score, ratingLabel]);

  const shareText = [
    'WordBloom 🌸',
    `Score: ${score} pts · ${found.length} words${pangramsFound ? ` · ${pangramsFound} pangram${pangramsFound > 1 ? 's' : ''}` : ''}`,
    `Rank: ${ratingLabel}`,
    '',
    'Play free at wordzingo.com/wordbloom',
  ].join('\n');

  // ── Flower renderer (used during play & finish) ──
  const renderFlower = (interactive) => (
    <div style={{ position: 'relative', width: 290, height: 290, margin: '0 auto' }}>
      {/* stem + pot behind the flower */}
      <div style={{
        position: 'absolute', left: '50%', bottom: -78, transform: 'translateX(-50%)',
        width: 14, height: 90, background: 'linear-gradient(90deg,#15803d,#22c55e,#15803d)',
        borderRadius: 8, zIndex: 0,
      }} />
      <div style={{
        position: 'absolute', left: 'calc(50% - 34px)', bottom: -34, transform: 'rotate(-35deg)',
        width: 34, height: 18, background: 'linear-gradient(90deg,#16a34a,#4ade80)',
        borderRadius: '0 60% 0 60%', zIndex: 0,
      }} />
      <div style={{
        position: 'absolute', left: 'calc(50% + 6px)', bottom: -52, transform: 'rotate(40deg)',
        width: 30, height: 16, background: 'linear-gradient(90deg,#16a34a,#4ade80)',
        borderRadius: '60% 0 60% 0', zIndex: 0,
      }} />
      <div style={{
        position: 'absolute', left: '50%', bottom: -120, transform: 'translateX(-50%)',
        width: 96, height: 56, zIndex: 0,
        background: 'linear-gradient(165deg,#e8854f,#c2410c)',
        clipPath: 'polygon(12% 0,88% 0,100% 100%,0 100%)',
        borderRadius: '0 0 12px 12px',
        boxShadow: 'inset 0 6px 0 rgba(255,255,255,0.18)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}>
        <span style={{ color: '#fff7ed', fontWeight: 900, fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', paddingBottom: 8, letterSpacing: '0.05em' }}>
          {phase === 'playing' || phase === 'done' ? `${score} pts` : 'WordBloom'}
        </span>
      </div>
      <div style={{
        position: 'absolute', left: '50%', bottom: -68, transform: 'translateX(-50%)',
        width: 108, height: 16, background: 'linear-gradient(90deg,#9a3412,#ea7a45,#9a3412)',
        borderRadius: 8, zIndex: 0,
      }} />

      {/* 6 petals */}
      {petals.map((ch, i) => {
        const { angle, t } = petalTransform(i);
        const isBonus = ch === bonus;
        return (
          <button
            key={i}
            onClick={() => interactive && addLetter(ch)}
            disabled={!interactive}
            style={{
              position: 'absolute', left: '50%', top: '50%',
              width: 78, height: 94, transform: t, transformOrigin: 'center',
              border: 'none', padding: 0, cursor: interactive ? 'pointer' : 'default',
              background: isBonus ? BONUS_BG : PETAL_BG,
              borderRadius: '50% 50% 50% 50% / 62% 62% 38% 38%',
              boxShadow: isBonus
                ? '0 0 0 3px #fff, 0 6px 20px rgba(245,158,11,0.55), 0 0 22px 4px rgba(245,158,11,0.45)'
                : '0 6px 16px rgba(236,72,153,0.35), inset 0 -6px 12px rgba(0,0,0,0.08)',
              transition: 'transform 0.12s ease',
              zIndex: 1,
            }}
          >
            <span style={{
              display: 'inline-block', transform: `rotate(${-angle}deg)`,
              color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 900,
              fontSize: '1.5rem', textTransform: 'uppercase',
              textShadow: '0 1px 3px rgba(0,0,0,0.25)',
            }}>
              {ch}
              {isBonus && (
                <span style={{
                  display: 'block', fontSize: '0.55rem', fontWeight: 800,
                  marginTop: -2, letterSpacing: '0.04em',
                }}>+5</span>
              )}
            </span>
          </button>
        );
      })}

      {/* center */}
      <button
        onClick={() => interactive && addLetter(center)}
        disabled={!interactive}
        style={{
          position: 'absolute', left: '50%', top: '50%',
          width: 86, height: 86, transform: 'translate(-50%, -50%)',
          borderRadius: '50%', border: '3px solid #fff',
          background: CENTER_BG, cursor: interactive ? 'pointer' : 'default',
          boxShadow: '0 6px 18px rgba(245,158,11,0.5), inset 0 -6px 12px rgba(0,0,0,0.12)',
          color: '#7c2d12', fontFamily: 'Nunito, sans-serif', fontWeight: 900,
          fontSize: '2rem', textTransform: 'uppercase', zIndex: 2,
        }}
      >
        {center}
      </button>
    </div>
  );

  // ── Rank ladder: shows the point target for each rank ──
  const renderLadder = () => (
    <div style={{ background: '#fff', borderRadius: 16, padding: '0.9rem 1.1rem', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '1.5rem' }}>
      <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, color: '#be185d', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
        🏆 Rank Ladder <span style={{ color: '#9ca3af', fontWeight: 700, fontSize: '0.78rem' }}>· hit the points to climb</span>
      </div>
      {rankRows.map((r) => (
        <div key={r.label} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.34rem 0.55rem', borderRadius: 9, marginBottom: 2,
          background: r.isCurrent ? '#fdf2f8' : 'transparent',
          border: r.isCurrent ? '1px solid #f9a8d4' : '1px solid transparent',
          opacity: r.reached ? 1 : 0.5,
        }}>
          <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, color: r.color, fontSize: '0.92rem' }}>
            {r.reached ? '✓ ' : '🔒 '}{r.label}
          </span>
          <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, color: '#475569', fontSize: '0.85rem' }}>
            {r.min === 0 ? 'start' : `${r.min}+ pts`}
          </span>
        </div>
      ))}
      <div style={{ fontFamily: 'Nunito, sans-serif', color: '#9ca3af', fontSize: '0.74rem', marginTop: '0.5rem', textAlign: 'center' }}>
        Targets are based on this flower's best {GUESSES} words.
      </div>
    </div>
  );

  return (
    <div className="game-page">
      <PageSEO page="wordbloom" />

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 1rem' }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        {/* Header */}
        <div className="text-center mb-3">
          <h1 style={{
            fontFamily: 'Nunito, sans-serif', fontWeight: 900,
            fontSize: 'clamp(2rem, 6vw, 2.8rem)',
            margin: 0,
            background: 'linear-gradient(90deg,#ec4899,#f59e0b)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            🌸 WordBloom
          </h1>
          <p style={{ color: '#6b7280', fontFamily: 'Nunito, sans-serif', margin: '0.3rem 0 0' }}>
            Build words using the petals — every word must use the center letter. Letters can repeat!
          </p>
        </div>

        {/* ── IDLE ── */}
        {(phase === 'idle' || phase === 'error') && (
          <div className="text-center">
            <div style={{ background: '#fff', borderRadius: 20, padding: '2rem', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', marginBottom: '1.5rem' }}>
              <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, color: '#be185d', marginBottom: '1.25rem' }}>How to Play</h3>
              <div className="row g-3 text-start">
                {[
                  ['🌼', 'Make words of 4+ letters from the 7 flower letters'],
                  ['🟡', 'Every word must use the center letter'],
                  ['🔁', 'Letters can be used more than once'],
                  ['🌟', 'The glowing gold petal scores a +5 bonus'],
                  ['🌸', 'Use all 7 letters for a Pangram (+7)'],
                  ['✍️', `You get ${GUESSES} words — make them count!`],
                  ['🏆', 'Climb from Seedling to Full Bloom — the more points, the higher your rank'],
                ].map(([icon, text]) => (
                  <div key={text} className="col-12 col-sm-6">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                      <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{icon}</span>
                      <span style={{ fontFamily: 'Nunito, sans-serif', color: '#374151', fontSize: '0.95rem' }}>{text}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {phase === 'error' && (
              <p style={{ color: '#dc2626', fontFamily: 'Nunito, sans-serif', marginBottom: '1rem' }}>
                Couldn’t load a puzzle. Make sure you’re connected and try again.
              </p>
            )}
            <button onClick={startGame} style={bigBtn}>🌱 Start Game</button>
          </div>
        )}

        {/* ── LOADING ── */}
        {phase === 'loading' && (
          <div className="text-center" style={{ padding: '3rem 0' }}>
            <div style={{ fontSize: '2.5rem' }}>🌸</div>
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#6b7280', marginTop: '0.5rem' }}>Growing your flower…</p>
          </div>
        )}

        {/* ── PLAYING / DONE ── */}
        {(phase === 'playing' || phase === 'done') && (
          <>
            {/* Stat row */}
            <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '1rem' }}>
              <div style={statCard}><div style={statLabel}>Score</div><div style={{ ...statVal, color: '#db2777' }}>{score}</div></div>
              <div style={statCard}><div style={statLabel}>Words</div><div style={{ ...statVal, color: '#7c3aed' }}>{found.length}</div></div>
              <div style={statCard}><div style={statLabel}>Words Left</div><div style={{ ...statVal, color: guessesLeft <= 3 ? '#dc2626' : '#16a34a' }}>{guessesLeft}</div></div>
            </div>

            {/* Bonus banner */}
            {phase === 'playing' && (
              <div style={{
                textAlign: 'center', marginBottom: '0.75rem', fontFamily: 'Nunito, sans-serif',
                fontWeight: 800, color: '#92400e', fontSize: '0.92rem',
                background: '#fffbeb', border: '2px solid #fcd34d', borderRadius: 12, padding: '0.5rem',
              }}>
                🌟 Bonus petal: <strong style={{ textTransform: 'uppercase' }}>{bonus}</strong> — use it for +5 points
              </div>
            )}

            {/* Word input display */}
            <div style={{
              minHeight: 54, background: '#fff', borderRadius: 14, marginBottom: '0.4rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', padding: '0.5rem 1rem', position: 'relative',
            }}>
              <span key={popKey} style={{
                fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.6rem',
                letterSpacing: '0.12em', textTransform: 'uppercase',
                color: input ? '#1e293b' : '#cbd5e1',
              }}>
                {input || 'type a word'}
              </span>
            </div>

            {/* Flash message */}
            <div style={{ height: 24, textAlign: 'center', marginBottom: '0.5rem' }}>
              {flash && (
                <span style={{
                  fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.95rem',
                  color: flash.type === 'good' ? '#16a34a' : '#dc2626',
                }}>
                  {flash.text}
                </span>
              )}
            </div>

            {/* Flower */}
            <div style={{ paddingBottom: 132, marginBottom: '1rem' }}>
              {renderFlower(phase === 'playing')}
            </div>

            {/* Controls */}
            {phase === 'playing' && (
              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'center', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                <button onClick={() => setInput(p => p.slice(0, -1))} style={ctrlBtn}>⌫ Delete</button>
                <button onClick={shuffle} style={ctrlBtn}>🔀 Shuffle</button>
                <button onClick={submit} style={{ ...ctrlBtn, background: '#ec4899', color: '#fff', border: 'none' }}>↵ Enter</button>
              </div>
            )}

            {/* Finish banner */}
            {phase === 'done' && (
              <div style={{ background: '#fff', borderRadius: 20, padding: '1.75rem', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '1rem', textAlign: 'center' }}>
                <div style={{ fontSize: '2.6rem' }}>🌸</div>
                <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, color: '#be185d', fontSize: '1.6rem', margin: '0.2rem 0' }}>
                  Garden Complete!
                </h2>
                <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.15rem', color: ratingColor, marginBottom: '0.3rem' }}>
                  {ratingLabel}
                </div>
                <p style={{ color: '#6b7280', fontFamily: 'Nunito, sans-serif', marginBottom: '1.25rem' }}>
                  You scored <strong>{score}</strong> points with <strong>{found.length}</strong> word{found.length === 1 ? '' : 's'}
                  {pangramsFound > 0 && <> and <strong>{pangramsFound}</strong> pangram{pangramsFound > 1 ? 's' : ''} 🌺</>}.
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={startGame} style={bigBtnSm}>🌱 New Flower</button>
                  <button onClick={() => setShowShare(true)} style={outlineBtn}>📤 Share</button>
                </div>
              </div>
            )}

            {/* Rank ladder */}
            {rankRows.length > 0 && renderLadder()}

            {/* Found words */}
            {found.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 16, padding: '1rem 1.25rem', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '1.5rem' }}>
                <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, color: '#be185d', marginBottom: '0.6rem', fontSize: '0.9rem' }}>
                  Found Words ({found.length})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {found.map((f) => (
                    <span key={f.word} title={`+${f.pts}`} style={{
                      fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.85rem',
                      textTransform: 'uppercase', padding: '0.2rem 0.6rem', borderRadius: 20,
                      background: f.pangram ? '#fce7f3' : f.usedBonus ? '#fef3c7' : '#f1f5f9',
                      color: f.pangram ? '#be185d' : f.usedBonus ? '#92400e' : '#475569',
                      border: f.pangram ? '1px solid #f9a8d4' : '1px solid transparent',
                    }}>
                      {f.word} <small style={{ opacity: 0.7 }}>+{f.pts}</small>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {showShare && (
          <ShareModal
            shareText={shareText}
            gameUrl="https://wordzingo.com/wordbloom"
            onClose={() => setShowShare(false)}
            onNewGame={startGame}
          />
        )}

        <SeoContent game="wordbloom" />
      </div>
    </div>
  );
}

const statCard  = { flex: 1, background: '#fff', borderRadius: 14, padding: '0.55rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' };
const statLabel = { fontSize: '0.62rem', color: '#9ca3af', fontFamily: 'Nunito, sans-serif', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' };
const statVal   = { fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.5rem', lineHeight: 1.1 };
const bigBtn    = { background: 'linear-gradient(90deg,#ec4899,#f59e0b)', color: '#fff', border: 'none', borderRadius: 50, padding: '0.9rem 3.5rem', fontWeight: 900, fontFamily: 'Nunito, sans-serif', fontSize: '1.2rem', cursor: 'pointer', boxShadow: '0 4px 18px rgba(236,72,153,0.4)' };
const bigBtnSm  = { background: 'linear-gradient(90deg,#ec4899,#f59e0b)', color: '#fff', border: 'none', borderRadius: 50, padding: '0.7rem 2rem', fontWeight: 800, fontFamily: 'Nunito, sans-serif', fontSize: '1rem', cursor: 'pointer', boxShadow: '0 3px 12px rgba(236,72,153,0.35)' };
const ctrlBtn   = { background: '#fff', color: '#475569', border: '2px solid #e2e8f0', borderRadius: 50, padding: '0.65rem 1.4rem', fontWeight: 800, fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', cursor: 'pointer' };
const outlineBtn= { background: '#fff', color: '#ec4899', border: '2px solid #ec4899', borderRadius: 50, padding: '0.7rem 2rem', fontWeight: 800, fontFamily: 'Nunito, sans-serif', fontSize: '1rem', cursor: 'pointer' };
